/**
 * Cookie Consent Manager
 * Manages user cookie preferences and consent state
 */

const STORAGE_KEY = 'brainstork_cookie_consent';

export class CookieManager {
  constructor() {
    this.preferences = this.loadPreferences();
  }

  /**
   * Load preferences from localStorage
   * @returns {Object|null} Cookie preferences or null if not set
   */
  loadPreferences() {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored ? JSON.parse(stored) : null;
    } catch (error) {
      console.error('Error loading cookie preferences:', error);
      return null;
    }
  }

  /**
   * Save preferences to localStorage
   * @param {Object} preferences - Cookie preferences object
   */
  savePreferences(preferences) {
    try {
      const preferencesWithTimestamp = {
        ...preferences,
        timestamp: new Date().toISOString()
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(preferencesWithTimestamp));
      this.preferences = preferencesWithTimestamp;
      console.log('Cookie preferences saved:', preferencesWithTimestamp);
    } catch (error) {
      console.error('Error saving cookie preferences:', error);
    }
  }

  /**
   * Check if user has given consent
   * @returns {boolean} True if consent was given
   */
  hasConsent() {
    return this.preferences !== null;
  }

  /**
   * Get specific cookie category preference
   * @param {string} category - Category name (essential, marketing, functional, analytics)
   * @returns {boolean} True if category is enabled
   */
  isEnabled(category) {
    if (!this.preferences) {
      return false;
    }
    // Essential cookies are always enabled
    if (category === 'essential') {
      return true;
    }
    return this.preferences[category] === true;
  }

  /**
   * Get all preferences
   * @returns {Object} Current preferences object
   */
  getPreferences() {
    return this.preferences || {
      essential: true,
      marketing: false,
      functional: false,
      analytics: false
    };
  }

  /**
   * Accept all cookies
   */
  acceptAll() {
    this.savePreferences({
      essential: true,
      marketing: true,
      functional: true,
      analytics: true
    });
  }

  /**
   * Reset preferences (clear consent)
   */
  reset() {
    try {
      localStorage.removeItem(STORAGE_KEY);
      this.preferences = null;
      console.log('Cookie preferences reset');
    } catch (error) {
      console.error('Error resetting cookie preferences:', error);
    }
  }
}

// Singleton instance
let instance = null;

/**
 * Get cookie manager instance
 * @returns {CookieManager} Cookie manager instance
 */
export function getCookieManager() {
  if (!instance) {
    instance = new CookieManager();
  }
  return instance;
}
